// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { PayableActions, ReceivableActions } from '@portal/actions';
import { getRouteStackPath } from '@portal/config/routes';

import { translate } from '@portal/services/i18n';
import { EyeOutlined, ProfileOutlined } from '@ant-design/icons';
import { moneyFormat } from '@portal/utils/format';
import DataTable from '@portal/components/DataTable/DataTable';
import { getEnumDescription } from '@portal/utils/enum';
import useReduxState from '@portal/hooks/useReduxState';
import ChargeDetailsModal from '@portal/components/ChargeDetailsModal/ChargeDetailsModal';
import { useLocation, useParams } from 'react-router';
import {
  PayableStatus,
  PaymentMode,
  ReceivableStatus,
  TransactionStatus,
  TransactionType,
} from '@portal/models/enumerators';
import { Link } from 'react-router-dom';
import { DateTime } from 'luxon';
import { Divider, Tooltip } from 'antd';
import { PaymentAccountType } from '@portal/models/enumerators/paymentAccount';
import useQuery from '@portal/hooks/useQuery';
import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import AdvancedCard from '@portal/components/AdvancedCard/AdvancedCard';
import truncateTextWithEllipsis from '@portal/utils/truncateTextWithEllipsis';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import { useNavigation } from '@portal/hooks/useNavigation';

const initialValues: advancedFilterModels.PaymentsFileAdvancedFilter = {
  orderBy: 'createdAt',
  page: 0,
  pageSize: 10,
  sort: 'desc',
  status: null,
  type: null,
  responsable: null,
  approver: null,
};

const FileDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigation();
  const params: { id: string } = useParams();
  const query = useQuery();
  const mode = query.get('mode') ?? PaymentMode.PAYABLE;
  const location = useLocation<{
    mode: PaymentMode;
  }>();
  const {
    payable: { payableReport, countPayable },
    receivable: { receivableReport, countReceivable },
    user: { me },
    paymentAccount: { selectedPaymentAccount },
  } = useReduxState();

  const [advancedFilters, setAdvancedFilters] = useState(initialValues);

  const [receivableDetailsModal, setReceivableDetailsModal] = useState({
    id: '',
    transactionId: '',
    visible: false,
    content: '',
    type: TransactionType.PIX,
  });

  const renderTooltipTitle = (status: number) => {
    switch (status) {
      case PayableStatus.ERROR:
        return translate('PAGES.PAYMENT.FILE_DETAILS.TOOLTIP.ERROR');
      case PayableStatus.PROCESSING:
        return translate('PAGES.PAYMENT.FILE_DETAILS.TOOLTIP.PROCESSING');
      case PayableStatus.ERROR_CORE_BANK:
        return translate('PAGES.PAYMENT.FILE_DETAILS.TOOLTIP.ERROR_CORE_BANK');
      default:
        return translate('PAGES.PAYMENT.FILE_DETAILS.TOOLTIP.UNDEFINED');
    }
  };

  const getPaymentRows = (filters: advancedFilterModels.baseFilter) => {
    const filtersCopy = {
      ...advancedFilters,
      ...filters,
    };
    setAdvancedFilters(filtersCopy);

    if (mode === PaymentMode.RECEIVABLE) {
      dispatch(
        ReceivableActions.getReceivablesByGroupId(params.id, filtersCopy)
      );
    }

    if (mode === PaymentMode.PAYABLE) {
      dispatch(
        PayableActions.getPayablesByGroupId(
          params.id,
          selectedPaymentAccount !== null
            ? selectedPaymentAccount.id
            : me.account.paymentAccounts.find(
                (account) => account.type === PaymentAccountType.ACCOUNT
              )?.id ?? '',
          filtersCopy
        )
      );
    }
  };

  useEffect(() => {
    switch (mode) {
      case PaymentMode.RECEIVABLE:
        dispatch(
          ReceivableActions.getReceivablesByGroupId(params.id, initialValues)
        );
        break;

      case PaymentMode.PAYABLE:
        dispatch(
          PayableActions.getPayablesByGroupId(
            params.id,
            selectedPaymentAccount !== null
              ? selectedPaymentAccount.id
              : me.account.paymentAccounts.find(
                  (account) => account.type === PaymentAccountType.ACCOUNT
                )?.id ?? '',
            initialValues
          )
        );
        break;

      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, params.id, location, selectedPaymentAccount?.id]);

  return (
    <>
      <AdvancedHeader
        showBreadCrumbs
        breadCrumbs={[
          {
            active: true,
            title: translate('COMPONENTS.BREADCRUMB.HOME'),
            url: getRouteStackPath('HOME', 'HOME_PAGE'),
          },
          {
            active: true,
            title: translate(`PAGES.PAYMENT.ACCOUNT_${mode}.TITLE`),
            url: getRouteStackPath(mode, `ACCOUNT_${mode}`),
          },
          {
            active: false,
            title: translate('PAGES.PAYMENT.FILE_DETAILS.TITLE'),
          },
        ]}
      />
      <div className="file-details">
        <AdvancedPanelContentHeader
          testId="file-details-header"
          title={translate('PAGES.PAYMENT.FILE_DETAILS.TITLE')}
          description={translate('PAGES.PAYMENT.FILE_DETAILS.DESCRIPTION')}
          goBackAction={() => navigate.back()}
        />
        <AdvancedCard testId="file-details-card">
          <DataTable
            columns={[
              {
                headerName:
                  mode === PaymentMode.PAYABLE
                    ? translate('PAGES.PAYMENT.FILE_DETAILS.COLUMNS.PAYEE_NAME')
                    : translate('PAGES.PAYMENT.FILE_DETAILS.COLUMNS.NAME'),
                flex: 1,
                field: 'payeeName',
                renderCell: (column) => (
                  <>
                    {column.row.payeeName && mode === PaymentMode.PAYABLE ? (
                      <Tooltip title={column.row.payeeName}>
                        {truncateTextWithEllipsis(column.row.payeeName, 20)}
                      </Tooltip>
                    ) : (
                      <div className="file-details__divider">
                        <Divider
                          type="horizontal"
                          className="file-details__divider__inner"
                        />
                      </div>
                    )}
                    {column.row.name && mode === PaymentMode.RECEIVABLE ? (
                      column.row.name
                    ) : column.row.payeeName &&
                      mode === PaymentMode.RECEIVABLE ? (
                      column.row.payeeName
                    ) : (
                      <div className="file-details__divider">
                        <Divider
                          type="horizontal"
                          className="file-details__divider__inner"
                        />
                      </div>
                    )}
                  </>
                ),
              },
              {
                headerName: translate(
                  'PAGES.PAYMENT.FILE_DETAILS.COLUMNS.AMOUNT'
                ),
                flex: 1,
                field: 'amount',
                renderCell: (column) => <>{moneyFormat(column.row.amount)}</>,
              },
              {
                headerName: translate(
                  'PAGES.PAYMENT.FILE_DETAILS.COLUMNS.TYPE'
                ),
                field: 'type',
                flex: 1,
                renderCell: (column) => (
                  <Tooltip
                    title={getEnumDescription(
                      column.row.type,
                      'TRANSACTION_TYPE'
                    )}
                  >
                    <ThemedText className="ellipsis">
                      {truncateTextWithEllipsis(
                        getEnumDescription(column.row.type, 'TRANSACTION_TYPE'),
                        25
                      )}
                    </ThemedText>
                  </Tooltip>
                ),
              },
              {
                headerName: translate(
                  'PAGES.PAYMENT.FILE_DETAILS.COLUMNS.STATUS'
                ),
                flex: 1,
                field: 'status',
                width: 150,
                renderCell: (column) =>
                  getEnumDescription(column.row.status, `${mode}_STATUS`),
              },
              {
                headerName: translate(
                  'PAGES.PAYMENT.FILE_DETAILS.COLUMNS.DUE_DATE'
                ),
                flex: 1,
                field: 'dueDate',
                headerAlign: 'center',
                align: 'center',
                width: 150,
                hide: mode === PaymentMode.PAYABLE,
                renderCell: (column) => (
                  <>
                    {column.row.dueDate ? (
                      DateTime.fromISO(column.row.dueDate).toLocaleString(
                        DateTime.DATE_SHORT
                      )
                    ) : (
                      <div className="file-details__divider">
                        <Divider
                          type="horizontal"
                          className="file-details__divider__inner"
                        />
                      </div>
                    )}
                  </>
                ),
              },
              {
                headerName: translate(
                  'PAGES.PAYMENT.FILE_DETAILS.COLUMNS.DETAILS'
                ),
                field: 'details',
                align: 'center',
                hide: mode === PaymentMode.PAYABLE,
                renderCell: (column) =>
                  (column.row.type === TransactionType.PIX ||
                    column.row.type === TransactionType.BILLET ||
                    column.row.type === TransactionType.BILLET_PIX) &&
                  column.row.status !== ReceivableStatus.CREATING &&
                  column.row.status !== ReceivableStatus.ERROR && (
                    <button
                      onClick={() => {
                        setReceivableDetailsModal({
                          ...column.row,
                          visible: true,
                        });
                      }}
                      className="transparent-button users__details--button"
                      type="button"
                    >
                      <ProfileOutlined />
                    </button>
                  ),
              },
              {
                headerName: translate(
                  'PAGES.PAYMENT.FILE_DETAILS.COLUMNS.DETAILS'
                ),
                align: 'center',
                field: 'action',
                renderCell: (column) => (
                  <div className="account-payable__details">
                    {(() => {
                      switch (mode) {
                        case PaymentMode.RECEIVABLE:
                          return Array.isArray(column.row.transactions) &&
                            column.row.transactions.filter(
                              (row) => row.status === TransactionStatus.EXECUTED
                            )[0]?.id ? (
                            <Link
                              to={{
                                pathname: `/inicio/detalhes/${
                                  column.row.transactions.filter(
                                    (row) =>
                                      row.status === TransactionStatus.EXECUTED
                                  )[0].id
                                }`,
                                state: {
                                  isReceivable: true,
                                },
                              }}
                            >
                              <button
                                className="transparent-button users__details--button"
                                type="button"
                              >
                                <EyeOutlined />
                              </button>
                            </Link>
                          ) : (
                            <EyeOutlined />
                          );

                        case PaymentMode.PAYABLE:
                          return column.row.transactionId &&
                            mode === PaymentMode.PAYABLE ? (
                            <Link
                              to={{
                                pathname: `/inicio/detalhes/${column.row.transactionId}`,
                                state: {
                                  isPayable: true,
                                },
                              }}
                            >
                              <button
                                className="transparent-button users__details--button"
                                type="button"
                              >
                                <EyeOutlined />
                              </button>
                            </Link>
                          ) : (
                            <Tooltip
                              title={renderTooltipTitle(column.row.status)}
                            >
                              <EyeOutlined />
                            </Tooltip>
                          );

                        default:
                          break;
                      }
                    })()}
                  </div>
                ),
              },
            ]}
            rows={(() => {
              switch (mode) {
                case PaymentMode.PAYABLE:
                  return payableReport ?? [];

                case PaymentMode.RECEIVABLE:
                  return receivableReport ?? [];

                default:
                  return [];
              }
            })()}
            page={advancedFilters.page}
            pageSize={advancedFilters.pageSize}
            onChange={(filters) => {
              const searchFilters = {
                ...advancedFilters,
                ...filters,
              };
              setAdvancedFilters(searchFilters);
              getPaymentRows(searchFilters);
            }}
            rowCount={(() => {
              switch (mode) {
                case PaymentMode.PAYABLE:
                  return countPayable;

                case PaymentMode.RECEIVABLE:
                  return countReceivable;

                default:
                  return 0;
              }
            })()}
            orderBy={advancedFilters.orderBy}
            sort={advancedFilters.sort}
          />
        </AdvancedCard>
        {mode === PaymentMode.RECEIVABLE && (
          <ChargeDetailsModal
            visible={receivableDetailsModal.visible}
            setVisible={(value) =>
              setReceivableDetailsModal((prev) => ({
                ...prev,
                visible: value,
              }))
            }
            receivable={receivableDetailsModal ?? null}
          />
        )}
      </div>
    </>
  );
};

export default FileDetails;
